<script setup lang="ts">
/**
 * La striscia che dice «è uscito un Cruscotto nuovo» a chi tiene la scheda aperta.
 *
 * Il fatto che la giustifica sta in testa a `lib/versione-nuova.ts`: Sabrina, tre volte lo
 * stesso rifiuto **dopo** la cura, perché la sua scheda era aperta dalle 09:02 e girava il
 * codice di quell'ora. Il router ricarica da sé al primo cambio di pagina; ma chi sta su una
 * pagina sola per un'ora — un viaggio a metà, un modulo scritto — non cambia pagina, e a quella
 * persona bisogna **dirlo**, lasciandole la scelta.
 *
 * ## Dove sta e perché proprio lì
 *
 * Sotto la barra superiore, **nel flusso** e non sopra il contenuto. Un cartellino fisso in
 * basso — la forma dei toast — starebbe esattamente sopra le cose che nel costruttore viaggi
 * vivono in fondo: la striscia dell'annulla e i comandi del viaggio. Un cartellino fisso in
 * alto coprirebbe la prima riga di qualunque tabella. Qui invece la striscia **si prende il suo
 * spazio** e il contenuto comincia sotto: non copre niente, costa quaranta pixel d'altezza, e li
 * restituisce al tocco sulla crocetta.
 *
 * È appiccicata sotto la barra (`position: sticky`) per la stessa ragione per cui esiste: chi
 * deve leggerla è in fondo a un elenco lungo da un'ora, e una riga che compare in cima alla
 * pagina mentre si è scorsi a tremila pixel non la vede nessuno. Dal flusso, non da fuori: lo
 * spazio lo riserva lo stesso.
 *
 * ## Quel che non fa
 *
 * Non ricarica da sé, mai: il costo di una ricarica non voluta è il lavoro non salvato di
 * un'altra persona, e non è un costo che si possa decidere da qui. Non ricorda la chiusura da
 * nessuna parte: chiusa, resta chiusa finché la scheda vive, e la ricarica automatica al
 * prossimo cambio di pagina (`router/index.ts`) succede comunque — la crocetta toglie la
 * striscia, non la cura.
 */

import { nextTick, ref, watch } from 'vue';
import { CircleFadingArrowUp, RefreshCw, X } from '@lucide/vue';
import Button from 'primevue/button';
import { versioneNuova } from '@/lib/versione-nuova';

/** Chiusa a mano, per questa scheda. Un `ref` e non `sessionStorage`: non deve sopravvivere. */
const chiusa = ref(false);

/**
 * Il testo per chi ascolta arriva un battito **dopo** la striscia.
 *
 * Una regione `role="status"` che entra nel documento con dentro già la sua frase non viene
 * annunciata: il lettore di schermo la registra e legge solo ciò che cambia dopo. È la stessa
 * nota di `TelaioCaricamento` e di `AssenzeView`, e qui conta più che altrove: la striscia
 * compare da sola, senza un gesto che la preceda, e chi non vede lo schermo non ha nessun
 * altro modo di sapere che c'è.
 */
const titolo = ref('');
const dettaglio = ref('');

watch(
  () => versioneNuova.value && !chiusa.value,
  (visibile) => {
    if (!visibile) {
      titolo.value = '';
      dettaglio.value = '';
      return;
    }
    void nextTick(() => {
      titolo.value = 'È uscita una versione nuova del Cruscotto.';
      dettaglio.value =
        'Si carica da sola al prossimo cambio di pagina. ' +
        'Se aggiorni adesso, quello che non hai ancora salvato in questa pagina si perde.';
    });
  },
  { immediate: true },
);

function aggiorna(): void {
  window.location.reload();
}

/**
 * Chiudere riporta il punto di lettura al contenuto.
 *
 * Il tasto che aveva il focus sparisce con la striscia; senza questa riga il focus cadrebbe sul
 * `body` e la prossima pressione del tabulatore ripartirebbe dall'inizio del documento, venti
 * voci di menu prima del punto in cui si stava lavorando. `#contenuto` è il `<main
 * tabindex="-1">` della cornice, raggiungibile da codice proprio per questo.
 */
function chiudi(): void {
  chiusa.value = true;
  document.getElementById('contenuto')?.focus({ preventScroll: true });
}
</script>

<template>
  <Transition name="avviso">
    <!--
      Il perimetro dell'annuncio è il **testo**, non la striscia: un `role="status"` sul
      contenitore reciterebbe anche i due comandi, che sono già raggiungibili col tabulatore e
      non sono una notizia. Il `<section>` porta il nome perché chi ci arriva tabulando sappia
      in che gruppo sta: «Aggiorna adesso» senza il contesto è un comando sospeso.
    -->
    <section v-if="versioneNuova && !chiusa" class="avviso" aria-label="Versione nuova del Cruscotto">
      <div class="avviso__dentro">
        <CircleFadingArrowUp class="avviso__segno" :size="20" :stroke-width="1.75" aria-hidden="true" />

        <div class="avviso__testo" role="status">
          <p class="avviso__titolo">{{ titolo }}</p>
          <p class="avviso__dettaglio">{{ dettaglio }}</p>
        </div>

        <!--
          Secondario e contornato, non pieno: è l'azione che la striscia offre, non quella che
          consiglia — la strada senza perdite è aspettare il prossimo cambio di pagina, e un
          tasto pieno su una striscia che dice «si perde» inviterebbe a premerlo senza leggere.
        -->
        <Button
          class="avviso__aggiorna"
          severity="secondary"
          outlined
          label="Aggiorna adesso"
          @click="aggiorna"
        >
          <template #icon>
            <RefreshCw :size="16" :stroke-width="2" aria-hidden="true" />
          </template>
        </Button>

        <button type="button" class="avviso__chiudi" aria-label="Chiudi l'avviso" @click="chiudi">
          <X :size="18" :stroke-width="2" aria-hidden="true" />
        </button>
      </div>
    </section>
  </Transition>
</template>

<style scoped>
/*
 * Appiccicata sotto la barra superiore, che è alta esattamente `--beste-layout-topbar` a ogni
 * larghezza. Sull'indice delle cose appiccicate (100), non su quello della barra (200): deve
 * stare sopra le intestazioni appiccicate dentro il contenuto, che vivono a 1 e 2, e sotto il
 * pannello mobile e il suo velo, che la rendono inerte insieme al resto della colonna.
 *
 * `display: grid` con una riga sola è l'impalcatura dell'animazione d'entrata: la riga va da
 * `0fr` a `1fr`, e il contenuto sotto scende invece di saltare. Lo stesso trucco di `TabellaDati`
 * per la striscia d'errore, e per la stessa ragione — un elemento che entra nel flusso senza
 * animare la propria altezza fa scattare tutta la pagina di quaranta pixel in un fotogramma.
 */
.avviso {
  position: sticky;
  top: var(--beste-layout-topbar);
  z-index: var(--beste-z-sticky);
  display: grid;
  grid-template-rows: 1fr;
  background: var(--beste-status-info-surface);
  color: var(--beste-status-info-text);
  border-bottom: 1px solid var(--beste-status-info-border);
}

.avviso__dentro {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  grid-template-areas: 'segno testo aggiorna chiudi';
  align-items: center;
  gap: var(--beste-space-8) var(--beste-space-12);
  min-height: 0;
  overflow: hidden;
  padding: var(--beste-space-8) var(--beste-space-16) var(--beste-space-8)
    var(--beste-layout-gutterDesktop);
}

.avviso__segno {
  grid-area: segno;
  color: var(--beste-status-info-solid);
}

.avviso__testo {
  grid-area: testo;
  min-width: 0;
}

.avviso__titolo,
.avviso__dettaglio {
  margin: 0;
  font-size: var(--beste-font-small-size);
  line-height: var(--beste-font-small-line);
  font-feature-settings: normal;
}

.avviso__titolo {
  font-weight: var(--beste-font-bodyStrong-weight);
}

/* Il dettaglio resta dello stesso colore del titolo, non del grigio «secondario»: su un fondale
   tinto quel grigio perde contrasto, e la frase sul lavoro che si perde è proprio quella che
   deve leggersi meglio. */
.avviso__dettaglio {
  text-wrap: pretty;
}

/* Quarantaquattro pixel per un dito, su entrambi i comandi: la striscia la toccano dai tablet
   della reception e dalla pianificazione, non solo col mouse. */
.avviso__aggiorna {
  grid-area: aggiorna;
  min-height: 44px;
  white-space: nowrap;
}

.avviso__chiudi {
  grid-area: chiudi;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: var(--beste-radius-md);
  color: var(--beste-status-info-text);
  cursor: pointer;
  transition:
    background-color var(--beste-duration-fast) var(--beste-ease-standard),
    color var(--beste-duration-fast) var(--beste-ease-standard);
}

/* Un alone al passaggio, come le icone della barra: un'aggiunta per chi ha il mouse, non la
   sola forma del comando — la crocetta è disegnata sempre. L'anello di focus viene dalla
   regola globale di `base.css`. */
.avviso__chiudi:hover,
.avviso__chiudi:active {
  background: color-mix(in srgb, var(--beste-status-info-border) 55%, transparent);
}

/* ------------------------------------------------------------------ entrata e uscita */

/*
 * Le durate vengono dai token, quindi `prefers-reduced-motion` le azzera in un punto solo e
 * qui non c'è niente da ricordarsi. L'uscita è più rapida dell'entrata, come la transizione
 * fra le schermate e per la stessa ragione.
 */
.avviso-enter-active {
  transition:
    grid-template-rows var(--beste-duration-normal) var(--beste-ease-enter),
    opacity var(--beste-duration-normal) var(--beste-ease-enter);
}

.avviso-leave-active {
  transition:
    grid-template-rows var(--beste-duration-fast) var(--beste-ease-exit),
    opacity var(--beste-duration-fast) var(--beste-ease-exit);
}

.avviso-enter-from,
.avviso-leave-to {
  grid-template-rows: 0fr;
  opacity: 0;
}

/* ------------------------------------------------------------------ schermi stretti */

@media (max-width: 1023px) {
  .avviso__dentro {
    padding-inline-start: var(--beste-layout-gutterMobile);
  }
}

/*
 * Sotto i 640 px il tasto scende sotto il testo e la crocetta resta in alto a destra, dove sta
 * su tutte le altre larghezze: un comando che cambia posto a seconda dello schermo è un comando
 * che si cerca. Il segno sale con il titolo, non resta a mezza altezza su due righe.
 */
@media (max-width: 639px) {
  .avviso__dentro {
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas:
      'segno testo chiudi'
      '. aggiorna chiudi';
    align-items: start;
  }

  .avviso__segno {
    margin-top: calc((var(--beste-font-small-line) - 20px) / 2);
  }

  .avviso__aggiorna {
    justify-self: start;
  }
}
</style>
